import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { motoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ImageLightbox } from '../components/ImageLightbox';
import { Bike, Plus, Trash2, Edit, Loader2, Gauge, ZoomIn, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { FotoUploader } from '../components/FotoUploader';
import { OcrCamera } from '../components/OcrCamera';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Moto() {
  const { refreshMotoList } = useAuth();
  const [motoList, setMotoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMoto, setEditingMoto] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [formData, setFormData] = useState({
    marca: '',
    modello: '',
    anno: new Date().getFullYear(),
    targa: '',
    km_attuali: 0,
    foto_urls: [],
    note: ''
  });

  useEffect(() => {
    loadMoto();
  }, []);

  const loadMoto = async () => {
    try {
      const response = await motoApi.getAll();
      setMotoList(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle moto');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      marca: '',
      modello: '',
      anno: new Date().getFullYear(),
      targa: '',
      km_attuali: 0,
      foto_urls: [],
      note: ''
    });
    setEditingMoto(null);
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const handleOpenDialog = (moto = null) => {
    if (moto) {
      setEditingMoto(moto);
      setFormData({
        marca: moto.marca,
        modello: moto.modello,
        anno: moto.anno,
        targa: moto.targa,
        km_attuali: moto.km_attuali,
        foto_urls: moto.foto_urls || [],
        note: moto.note || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.marca || !formData.modello || !formData.targa) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      if (editingMoto) {
        await motoApi.update(editingMoto.id, formData);
        toast.success('Moto aggiornata');
      } else {
        await motoApi.create(formData);
        toast.success('Moto aggiunta');
      }
      setDialogOpen(false);
      resetForm();
      await loadMoto();
      await refreshMotoList(); // Sync global motoList in AuthContext
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa moto?')) return;
    
    try {
      await motoApi.delete(id);
      toast.success('Moto eliminata');
      await loadMoto();
      await refreshMotoList(); // Sync global motoList in AuthContext
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="moto-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Le Mie Moto</h1>
            <p className="page-subtitle">Gestisci il tuo garage</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-12 px-6"
                data-testid="add-moto-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Aggiungi Moto
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                  {editingMoto ? 'Modifica Moto' : 'Nuova Moto'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Marca *</Label>
                    <Input
                      value={formData.marca}
                      onChange={(e) => setFormData({...formData, marca: e.target.value})}
                      placeholder="es. Ducati"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="moto-marca"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Modello *</Label>
                    <Input
                      value={formData.modello}
                      onChange={(e) => setFormData({...formData, modello: e.target.value})}
                      placeholder="es. Monster 821"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="moto-modello"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Anno *</Label>
                    <Input
                      type="number"
                      value={formData.anno}
                      onChange={(e) => setFormData({...formData, anno: parseInt(e.target.value)})}
                      onFocus={(e) => e.target.select()}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="moto-anno"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Targa *</Label>
                    <Input
                      value={formData.targa}
                      onChange={(e) => setFormData({...formData, targa: e.target.value.toUpperCase()})}
                      placeholder="es. AB123CD"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono uppercase"
                      data-testid="moto-targa"
                    />
                  </div>
                </div>

                <div>
                  <Label className="form-label">Km Attuali</Label>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      value={formData.km_attuali}
                      onChange={(e) => setFormData({...formData, km_attuali: parseInt(e.target.value) || 0})}
                      onFocus={(e) => e.target.select()}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="moto-km"
                    />
                    <OcrCamera 
                      type="odometer"
                      buttonText="📷 Scatta foto contachilometri"
                      buttonClassName="w-full h-10 bg-[#1a1a1a] hover:bg-[#262626] text-[#9CA3AF] hover:text-white border border-[#262626] hover:border-[#FF5F1F] transition-colors"
                      onDataExtracted={(data) => {
                        if (data.km !== null) {
                          setFormData(prev => ({...prev, km_attuali: data.km}));
                          toast.success(`Km aggiornati a ${data.km.toLocaleString()}`);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Foto Upload */}
                <div>
                  <Label className="form-label">Foto</Label>
                  <FotoUploader
                    fotoUrls={formData.foto_urls}
                    setFotoUrls={(urls) => {
                      if (typeof urls === 'function') {
                        setFormData(prev => ({...prev, foto_urls: urls(prev.foto_urls)}));
                      } else {
                        setFormData({...formData, foto_urls: urls});
                      }
                    }}
                    maxPhotos={5}
                  />
                </div>

                <div>
                  <Label className="form-label">Note</Label>
                  <Input
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    placeholder="Note aggiuntive..."
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                    data-testid="moto-note"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 h-11 border-[#262626] text-white hover:bg-white/5 uppercase tracking-wider rounded-sm"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 h-11 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
                    data-testid="moto-submit"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingMoto ? 'Salva' : 'Aggiungi')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {motoList.length === 0 ? (
          <div className="vault-card text-center py-16">
            <Bike className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Nessuna moto registrata</h3>
            <p className="text-[#9CA3AF] mb-6">Aggiungi la tua prima moto per iniziare</p>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-11 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Aggiungi Moto
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {motoList.map((moto) => (
              <div
                key={moto.id}
                className="vault-card group relative overflow-hidden"
                data-testid={`moto-card-${moto.id}`}
              >
                {moto.foto_urls && moto.foto_urls.length > 0 && (
                  <div className="absolute inset-0 opacity-30">
                    <img 
                      src={moto.foto_urls[0].startsWith('/api') ? `${BACKEND_URL}${moto.foto_urls[0]}` : moto.foto_urls[0]} 
                      alt={`${moto.marca} ${moto.modello}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                  </div>
                )}
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#FF5F1F]/10 flex items-center justify-center">
                      <Bike className="w-6 h-6 text-[#FF5F1F]" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenDialog(moto)}
                        className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                        data-testid={`edit-moto-${moto.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(moto.id)}
                        className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                        data-testid={`delete-moto-${moto.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <h3 className="font-['Teko'] text-2xl text-white uppercase mb-1">
                    {moto.marca} {moto.modello}
                  </h3>
                  <p className="text-[#9CA3AF] font-mono text-sm mb-4">{moto.targa}</p>

                  {moto.foto_urls && moto.foto_urls.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 mb-4">
                      {moto.foto_urls.slice(0, 3).map((url, idx) => (
                        <div 
                          key={idx} 
                          className="relative cursor-pointer group/img"
                          onClick={() => openLightbox(moto.foto_urls, idx)}
                        >
                          <img 
                            src={getImageUrl(url)} 
                            alt="" 
                            className="w-full h-12 object-cover border border-[#262626]" 
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                            <ZoomIn className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-6 pt-4 border-t border-[#262626]">
                    <div className="flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-[#9CA3AF]" />
                      <span className="text-[#FF5F1F] font-mono text-lg">{formatNumber(moto.km_attuali)}</span>
                      <span className="text-[#9CA3AF] text-xs uppercase">km</span>
                    </div>
                    <div className="text-[#9CA3AF] font-mono text-sm">
                      {moto.anno}
                    </div>
                  </div>

                  {moto.note && (
                    <p className="text-[#9CA3AF] text-sm mt-4 line-clamp-2">{moto.note}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Layout>
  );
}
